import type { ServiceError as SharedServiceError } from '@kit/shared/result';

/**
 * Shared error + Result types for all @kit/marketplace services.
 *
 * ## `ServiceError`
 *
 * Re-exported from `@kit/shared/result` so a single canonical type is used
 * across the codebase (80+ services extend `BaseService<Database>` which
 * also uses this shape). Constructor is
 * `(code, message, originalError?, details?)` — marketplace callers
 * historically passed `(code, message, cause)`, which remains positionally
 * compatible. The shared class also exposes `.toJSON()`, `.is(code)`, and
 * static helpers (`ServiceError.notFound(...)`, etc.).
 *
 * Field-name note: the third positional arg was named `cause` in the
 * marketplace-local class; in the shared class it's `originalError`. No
 * marketplace code reads either field directly (only `.code` / `.message`
 * are consumed), so this rename is invisible to existing callers.
 *
 * ## `Result`
 *
 * Marketplace ships a discriminated union (see below). The shared
 * package's `Result` is a class with methods (`.isSuccess`, `.getValue()`,
 * `.map()`, `.flatMap()`, etc.) — the two shapes are NOT interchangeable.
 *
 * Migrating marketplace to the shared class would require rewriting every
 * consumer that does `if (result.success) { result.data }` — ~50+ call
 * sites across services, server actions, tests, and route handlers. Out of
 * scope for the current hardening pass; tracked as a follow-up.
 *
 * Public surface — consumers continue to do:
 *   import { ServiceError, type Result } from '@kit/marketplace';
 */

export { ServiceError } from '@kit/shared/result';

export type Result<T, E = SharedServiceError> =
  | { success: true; data: T }
  | { success: false; error: E };
